import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import {
  XMarkIcon,
  InformationCircleIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../../ui/LoadingSpinner';
import { shipmentsService } from '../../../services/logistics/shipments';
import { clientsService } from '../../../services/logistics/clients';
import { shipmentCostingsService } from '../../../services/logistics/shipment-costings';
import { invoicesService } from '../../../services/accounting/invoices';
import { truckTypesService } from '../../../services/logistics/truck-types';
import { formatCurrency, ensureEnglishNumerals } from '../../../utils/formatting';
import toast from 'react-hot-toast';

/**
 * CreateInvoiceModal Component
 * 
 * Comprehensive invoice creation modal that handles:
 * - Multi-step invoice creation process
 * - Shipment selection with client grouping
 * - Individual shipment cost management
 * - Additional costs and charges
 * - Invoice details and validation
 * - Real-time total calculations
 * 
 * Features:
 * - Three-step wizard interface
 * - Collapsible sections for better UX
 * - Bulk selection capabilities
 * - Integration with shipment costing
 * - VAT calculations (15% Saudi rate)
 * - Professional invoice generation
 * 
 * Translation Support:
 * - All user-facing text uses i18n translation keys
 * - Supports both Arabic and English interfaces
 * - Maintains RTL compatibility
 */
const CreateInvoiceModal = ({ onClose, onSuccess }) => {
  const { t, i18n } = useTranslation();
  const { currentCompany } = useAuth();

  // Helper function to get truck type name
  const getTruckTypeName = (truckTypeValue) => {
    if (!truckTypeValue) return null;
    
    const truckType = truckTypes.find(tt => tt.value === truckTypeValue);
    if (truckType && truckType.name) {
      return truckType.name[i18n.language] || truckType.name.en;
    }
    
    return truckTypeValue;
  };
  
  // State management
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [deliveredShipments, setDeliveredShipments] = useState([]);
  const [selectedShipments, setSelectedShipments] = useState([]);
  const [clients, setClients] = useState([]);
  const [truckTypes, setTruckTypes] = useState([]);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [shipmentCosts, setShipmentCosts] = useState({});
  
  // Invoice data state
  const [invoiceData, setInvoiceData] = useState({
    clientId: '',
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    description: '',
    notes: '',
    vatId: ''
  });

  /**
   * Load delivered shipments with client information
   * Filters shipments that are available for invoicing
   */
  const loadDeliveredShipments = useCallback(async () => {
    if (!currentCompany?.id) return;
    try {
      setLoading(true);
      
      // Load clients, shipments, and truck types in parallel
      const [clientsData, availableShipments, truckTypesData] = await Promise.all([
        clientsService.getClients(currentCompany.id),
        shipmentsService.getAvailableShipments(currentCompany.id),
        truckTypesService.getTruckTypes(currentCompany.id)
      ]);
      
      setClients(clientsData);
      setTruckTypes(truckTypesData);
      
      // Filter shipments with cost > 0 and enhance with client info
      const shipmentsWithDetails = availableShipments
        .filter(shipment => shipment.cost > 0)
        .sort((a, b) => {
          // Sort by date (latest first) before processing
          const dateA = a.deliveryDate || a.deliveredAt || a.deliveredTime || a.createdAt || a.shipmentDate;
          const dateB = b.deliveryDate || b.deliveredAt || b.deliveredTime || b.createdAt || b.shipmentDate;
          const parsedDateA = dateA?.toDate ? dateA.toDate() : new Date(dateA || 0);
          const parsedDateB = dateB?.toDate ? dateB.toDate() : new Date(dateB || 0);
          return parsedDateB - parsedDateA; // Latest first
        })
        .map(shipment => {
          const client = clientsData.find(c => c.id === shipment.clientId);
          // Use deliveryDate, deliveredAt, deliveredTime, createdAt, or shipmentDate for displayDateTime
          let displayDateTime = shipment.deliveryDate || shipment.deliveredAt || shipment.deliveredTime || shipment.createdAt || shipment.shipmentDate;
          if (displayDateTime && displayDateTime.toDate) displayDateTime = displayDateTime.toDate();
          if (typeof displayDateTime === 'string' || typeof displayDateTime === 'number') displayDateTime = new Date(displayDateTime);
            
          // Calculate total including itemized costs and backhaul
          const itemizedTotal = (shipment.itemizedCosts || []).reduce((sum, item) => {
            return sum + (item.total || ((item.quantity || 0) * (item.unitPrice || 0)) || 0);
          }, 0);
          const backhaulAmount = shipment.backhaulPrice || 0;
          const totalWithExtras = (shipment.cost || 0) + itemizedTotal + backhaulAmount;
          
          return {
            ...shipment,
            clientName: client?.name || client?.companyName || `Client ${shipment.clientId || 'Unknown'}`,
            clientId: client?.id || shipment.clientId || '',
            baseAmount: shipment.cost || 0,
            itemizedCosts: shipment.itemizedCosts || [],
            backhaulPrice: backhaulAmount,
            backhaulDescription: shipment.backhaulDescription || '',
            vatAmount: totalWithExtras * 0.15,
            totalAmount: totalWithExtras * 1.15,
            displayDateTime: displayDateTime || null,
            // Ensure all required fields have default values
            trackingNumber: shipment.trackingNumber || '',
            origin: shipment.origin || shipment.originCity || '',
            destination: shipment.destination || shipment.destinationCity || '',
            serviceType: shipment.serviceType || 'dry',
            tripNumber: shipment.tripNumber || '',
            cost: shipment.cost || 0
          };
        });

      setDeliveredShipments(shipmentsWithDetails);
      
      // Initialize collapsed sections with ALL sections collapsed by default
      const initialCollapsedSections = {};
      const uniqueClientIds = [...new Set(shipmentsWithDetails.map(s => s.clientId))];
      uniqueClientIds.forEach(clientId => {
        if (clientId) {
          initialCollapsedSections[`${clientId}-available`] = true;
          initialCollapsedSections[`${clientId}-draft`] = true;
          initialCollapsedSections[`${clientId}-invoiced`] = true;
        }
      });
      setCollapsedSections(initialCollapsedSections);
    } catch (error) {
      console.error('Error loading delivered shipments:', error);
      toast.error(t('common.error_loading_data', 'Failed to load data'));
    } finally {
      setLoading(false);
    }
  }, [currentCompany?.id, t]);

  // Load shipments on component mount
  useEffect(() => {
    if (currentCompany?.id) {
      loadDeliveredShipments();
    }
  }, [currentCompany?.id, loadDeliveredShipments]);

  /**
   * Handle shipment selection/deselection
   * Loads costing data for selected shipments
   */
  const handleShipmentSelection = async (shipment, selected) => {
    // Check if any other client has selected shipments (this client would be disabled)
    const hasOtherClientSelected = selectedShipments.some(s => s.clientId !== shipment.clientId);
    const isThisClientSelected = selectedShipments.some(s => s.clientId === shipment.clientId);
    const isDisabled = hasOtherClientSelected && !isThisClientSelected;
    
    // Prevent selection if this client is disabled
    if (selected && isDisabled) {
      return;
    }
    
    if (selected) {
      // Add shipment to selection
      const updatedSelectedShipments = [...selectedShipments, shipment];
      setSelectedShipments(updatedSelectedShipments);
      
      // Auto-set client if not set and all selected shipments are from same client
      if (!invoiceData.clientId && shipment.clientId) {
        setInvoiceData(prev => ({ ...prev, clientId: shipment.clientId }));
      }
      
      // Load costing data for this shipment
      try {
        const costing = await shipmentCostingsService.getCostingByShipment(shipment.id);
        if (costing) {
          // Initialize shipment costs with existing costing data
          const existingCosts = [
            ...(costing.stops || []).map(stop => ({
              id: `stop-${Date.now()}-${Math.random()}`,
              description: stop.description || 'Additional Stop',
              amount: stop.amount || 0,
              type: 'stop',
              source: 'existing'
            })),
            ...(costing.otherCosts || []).map(cost => ({
              id: `other-${Date.now()}-${Math.random()}`,
              description: cost.description || 'Other Cost',
              amount: cost.amount || 0,
              type: 'other',
              source: 'existing'
            }))
          ];
          
          setShipmentCosts(prev => ({
            ...prev,
            [shipment.id]: {
              baseCost: shipment.cost || 0,
              additionalCosts: existingCosts,
              fuel: costing.fuel || 0,
              maintenance: costing.maintenance || 0,
              commission: costing.commission || 0,
              costingComplete: !!(costing.fuel !== undefined && costing.maintenance !== undefined && costing.commission !== undefined)
            }
          }));
        } else {
          // No existing costing data
          setShipmentCosts(prev => ({
            ...prev,
            [shipment.id]: {
              baseCost: shipment.cost || 0,
              additionalCosts: [],
              fuel: 0,
              maintenance: 0,
              commission: 0,
              costingComplete: false
            }
          }));
        }
      } catch (error) {
        console.error('Error loading costing for shipment:', shipment.id, error);
        // Initialize with empty costing
        setShipmentCosts(prev => ({
          ...prev,
          [shipment.id]: {
            baseCost: shipment.cost || 0,
            additionalCosts: [],
            fuel: 0,
            maintenance: 0,
            commission: 0,
            costingComplete: false
          }
        }));
      }
    } else {
      // Remove shipment from selection
      setSelectedShipments(selectedShipments.filter(s => s.id !== shipment.id));
      
      // Remove shipment costs
      setShipmentCosts(prev => {
        const updated = { ...prev };
        delete updated[shipment.id];
        return updated;
      });
    }
  };

  /**
   * Toggle collapsed section visibility
   */
  const toggleSection = (clientId, section) => {
    const key = `${clientId}-${section}`;
    setCollapsedSections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  /**
   * Handle bulk selection for a specific client
   */
  const handleSelectAllForClient = async (clientId, shipments, selectAll) => {
    // Check if any other client has selected shipments (this client would be disabled)
    const hasOtherClientSelected = selectedShipments.some(s => s.clientId !== clientId);
    const isThisClientSelected = selectedShipments.some(s => s.clientId === clientId);
    const isDisabled = hasOtherClientSelected && !isThisClientSelected;
    
    // Prevent selection if this client is disabled
    if (selectAll && isDisabled) {
      return;
    }
    
    // Only allow selection of available and draft shipments
    const selectableShipments = shipments.filter(s => s.isAvailable);
    
    if (selectAll) {
      // Add all selectable shipments for this client
      const newSelections = selectableShipments.filter(s => 
        !selectedShipments.some(selected => selected.id === s.id)
      );
      
      // Update selected shipments immediately
      setSelectedShipments(prev => [...prev, ...newSelections]);
      
      // Load costing data for all new shipments in parallel
      const costingPromises = newSelections.map(async (shipment) => {
        try {
          const costing = await shipmentCostingsService.getCostingByShipment(shipment.id);
          if (costing) {
            const additionalCosts = [
              ...(costing.stops || []).map(stop => ({
                id: `stop-${Date.now()}-${Math.random()}`,
                description: stop.description || 'Additional Stop',
                amount: stop.amount || 0,
                quantity: 1,
                type: 'stop',
                source: 'existing'
              })),
              ...(costing.otherCosts || []).map(cost => ({
                id: `other-${Date.now()}-${Math.random()}`,
                description: cost.description || 'Other Cost',
                amount: cost.amount || 0,
                quantity: 1,
                type: 'other',
                source: 'existing'
              }))
            ];
            
            return {
              shipmentId: shipment.id,
              costingData: {
                baseCost: costing.baseAmount || shipment.baseAmount || shipment.cost || 0,
                fuel: costing.fuel || 0,
                maintenance: costing.maintenance || 0,
                commission: costing.commission || 0,
                additionalCosts,
                costingComplete: true
              }
            };
          }
          return {
            shipmentId: shipment.id,
            costingData: {
              baseCost: shipment.baseAmount || 0,
              fuel: 0,
              maintenance: 0,
              commission: 0,
              additionalCosts: [],
              costingComplete: false
            }
          };
        } catch (error) {
          console.error('Error loading costing for shipment:', shipment.id, error);
          return {
            shipmentId: shipment.id,
            costingData: {
              baseCost: shipment.baseAmount || shipment.cost || 0,
              fuel: 0,
              maintenance: 0,
              commission: 0,
              additionalCosts: [],
              costingComplete: false
            }
          };
        }
      });
      
      // Wait for all costing data to load
      const costingResults = await Promise.all(costingPromises);
      
      // Update shipment costs state
      setShipmentCosts(prev => {
        const newCosts = { ...prev };
        costingResults.forEach(result => {
          newCosts[result.shipmentId] = result.costingData;
        });
        return newCosts;
      });
      
      // Auto-set client if not set
      if (!invoiceData.clientId && clientId) {
        setInvoiceData(prev => ({ ...prev, clientId }));
      }
    } else {
      // Remove all selected shipments for this client
      const shipmentIdsToRemove = selectableShipments.map(s => s.id);
      
      // Update selected shipments
      setSelectedShipments(prev => prev.filter(s => !shipmentIdsToRemove.includes(s.id)));
      
      // Remove costing data for removed shipments
      setShipmentCosts(prev => {
        const newCosts = { ...prev };
        shipmentIdsToRemove.forEach(shipmentId => {
          delete newCosts[shipmentId];
        });
        return newCosts;
      });
    }
  };

  /**
   * Shipment cost management functions
   */
  const addShipmentCost = (shipmentId, costType = 'other') => {
    const newCost = {
      id: Date.now().toString(),
      description: '',
      amount: 0,
      quantity: 1,
      type: costType,
      source: 'new'
    };
    
    setShipmentCosts(prev => ({
      ...prev,
      [shipmentId]: {
        ...prev[shipmentId],
        additionalCosts: [...(prev[shipmentId]?.additionalCosts || []), newCost]
      }
    }));
  };

  const updateShipmentCost = (shipmentId, costId, field, value) => {
    setShipmentCosts(prev => ({
      ...prev,
      [shipmentId]: {
        ...prev[shipmentId],
        additionalCosts: (prev[shipmentId]?.additionalCosts || []).map(cost =>
          cost.id === costId ? { ...cost, [field]: value === '' ? 0 : value } : cost
        )
      }
    }));
  };

  const removeShipmentCost = (shipmentId, costId) => {
    setShipmentCosts(prev => ({
      ...prev,
      [shipmentId]: {
        ...prev[shipmentId],
        additionalCosts: (prev[shipmentId]?.additionalCosts || []).filter(cost => cost.id !== costId)
      }
    }));
  };

  /**
   * Calculate totals per shipment and overall invoice
   */
  const calculateShipmentTotals = (shipmentId) => {
    const shipment = selectedShipments.find(s => s.id === shipmentId);
    const shipmentCost = shipmentCosts[shipmentId];
    if (!shipmentCost && !shipment) return { subtotal: 0, vatAmount: 0, total: 0 };
    
    // Calculate additional costs from manual entries
    const additionalCostsTotal = (shipmentCost?.additionalCosts || []).reduce((sum, cost) => sum + (cost.amount || 0), 0);
    
    // Include itemized costs from shipment
    const itemizedCostsTotal = (shipment?.itemizedCosts || []).reduce((sum, item) => {
      return sum + (item.total || (item.quantity * item.unitPrice) || 0);
    }, 0);
    
    // Include backhaul if present
    const backhaulTotal = shipment?.backhaulPrice || 0;
    
    const baseAmount = shipmentCost?.baseCost || shipment?.baseAmount || shipment?.cost || 0;
    const subtotal = baseAmount + additionalCostsTotal + itemizedCostsTotal + backhaulTotal;
    const vatAmount = subtotal * 0.15;
    const total = subtotal + vatAmount;
    
    return { subtotal, vatAmount, total };
  };

  const calculateInvoiceTotals = () => {
    let overallSubtotal = 0;
    let overallVatAmount = 0;
    let overallTotal = 0;
    
    selectedShipments.forEach(shipment => {
      const { subtotal, vatAmount, total } = calculateShipmentTotals(shipment.id);
      overallSubtotal += subtotal;
      overallVatAmount += vatAmount;
      overallTotal += total;
    });
    
    return { 
      subtotal: overallSubtotal, 
      vatAmount: overallVatAmount, 
      total: overallTotal 
    };
  };

  /**
   * Generate unique invoice number
   */
  const generateInvoiceNumber = () => {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `INV-${year}${month}-${random}`;
  };

  /**
   * Clean undefined values from object recursively
   */
  const cleanUndefinedValues = (obj) => {
    if (obj === null || obj === undefined) return null;
    if (typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) {
      return obj.map(item => cleanUndefinedValues(item)).filter(item => item !== undefined);
    }
    
    const cleaned = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = cleanUndefinedValues(value);
      }
    }
    return cleaned;
  };

  /**
   * Create invoice with comprehensive validation and error handling
   */
  const createInvoice = async () => {
    // Prevent duplicate submissions
    if (loading) {
      console.log('⚠️ Invoice creation already in progress, ignoring duplicate request');
      return;
    }
    
    if (selectedShipments.length === 0) {
      toast.error(t('invoices.error.no_shipments_selected', 'Please select at least one shipment'));
      return;
    }

    if (!invoiceData.clientId) {
      toast.error(t('invoices.error.no_client_selected', 'Please select a client'));
      return;
    }

    try {
      setLoading(true);
      
      // Validation checks
      if (!currentCompany?.id) {
        throw new Error('No company selected');
      }
      
      if (!invoiceData.clientId) {
        throw new Error('No client selected');
      }
      
      if (selectedShipments.length === 0) {
        throw new Error('No shipments selected');
      }
      
      // Validate that all selected shipments have valid data
      const invalidShipments = selectedShipments.filter(shipment => 
        !shipment.id || (!shipment.cost && shipment.cost !== 0)
      );
      
      if (invalidShipments.length > 0) {
        console.error('❌ Invalid shipments found:', invalidShipments);
        throw new Error(`Invalid shipment data found for ${invalidShipments.length} shipment(s)`);
      }
      
      console.log('✅ Starting invoice creation...');
      console.log('🏢 Company ID:', currentCompany.id);
      console.log('👤 Client ID:', invoiceData.clientId);
      console.log('📦 Selected shipments:', selectedShipments.length);
      
      const { subtotal, vatAmount, total } = calculateInvoiceTotals();
      
      console.log('💰 Calculated totals:', { subtotal, vatAmount, total });
      
      // Collect all additional costs from shipments
      const allAdditionalCosts = [];
      selectedShipments.forEach(shipment => {
        const shipmentCost = shipmentCosts[shipment.id];
        if (shipmentCost?.additionalCosts) {
          shipmentCost.additionalCosts.forEach(cost => {
            allAdditionalCosts.push({
              ...cost,
              shipmentId: shipment.id,
              // Ensure no undefined values
              description: cost.description || '',
              amount: cost.amount || 0,
              quantity: cost.quantity || 1,
              type: cost.type || 'other',
              source: cost.source || 'manual'
            });
          });
        }
      });
      
      // Generate invoice number
      const invoiceNumber = generateInvoiceNumber();
      console.log('🆔 Generated invoice number:', invoiceNumber);
      
      const newInvoice = {
        invoiceNumber: invoiceNumber,
        companyId: currentCompany.id,
        clientId: invoiceData.clientId,
        shipmentIds: selectedShipments.map(s => s.id),
        issueDate: invoiceData.issueDate || new Date().toISOString().split('T')[0],
        dueDate: invoiceData.dueDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        description: invoiceData.description || `Invoice for ${selectedShipments.length} shipment(s)`,
        notes: invoiceData.notes || '',
        vatId: invoiceData.vatId || '',
        additionalCosts: allAdditionalCosts,
        shipmentCosts: shipmentCosts,
        subtotal: Math.round(subtotal * 100) / 100,
        vatAmount: Math.round(vatAmount * 100) / 100,
        vatRate: 0.15,
        finalTotal: Math.round(total * 100) / 100,
        total: Math.round(total * 100) / 100,
        status: 'draft',
        paymentStatus: 'unpaid',
        paidAmount: 0,
        createdBy: currentCompany.id,
        items: selectedShipments.map(shipment => {
          const shipmentCost = shipmentCosts[shipment.id];
          const shipmentAdditionalCosts = shipmentCost?.additionalCosts || [];
          
          // Include itemized costs from the shipment itself
          const shipmentItemizedCosts = shipment.itemizedCosts || [];
          const allAdditionalCosts = [
            ...shipmentAdditionalCosts,
            ...shipmentItemizedCosts.map(item => ({
              id: `itemized-${item.id || Date.now()}`,
              description: item.description || '',
              amount: item.total || (item.quantity * item.unitPrice) || 0,
              quantity: item.quantity || 1,
              unitPrice: item.unitPrice || 0,
              type: 'itemized',
              source: 'shipment'
            }))
          ];
          
          const additionalCostTotal = allAdditionalCosts.reduce((sum, cost) => sum + (cost.amount || 0), 0);
          const baseAmount = shipmentCost?.baseCost || shipment.baseAmount || shipment.cost || 0;
          const shipmentSubtotal = baseAmount + additionalCostTotal;
          
          // Include backhaul if present
          const backhaulAmount = shipment.backhaulPrice || 0;
          const totalWithBackhaul = shipmentSubtotal + backhaulAmount;
          
          return {
            description: `Shipment ${shipment.trackingNumber || shipment.id || 'Unknown'} - ${shipment.origin || 'Unknown'} to ${shipment.destination || 'Unknown'}`,
            comments: (() => {
              const serviceType = shipment.serviceType || '-';
              const truckTypeName = getTruckTypeName(shipment.truckType);
              const truckInfo = truckTypeName ? `- ${truckTypeName}` : '';
              return `Trip: ${shipment.tripNumber || '-'}, Date/Time: ${shipment.displayDateTime ? new Date(shipment.displayDateTime).toLocaleString() : '-'}, Service: ${serviceType}${truckInfo}`;
            })(),
            quantity: 1,
            unitPrice: baseAmount,
            total: Math.round(totalWithBackhaul * 100) / 100,
            shipmentId: shipment.id || '',
            trackingNumber: shipment.trackingNumber || '',
            origin: shipment.origin || '',
            destination: shipment.destination || '',
            serviceType: shipment.serviceType || '',
            tripNumber: shipment.tripNumber || '',
            displayDateTime: shipment.displayDateTime || null,
            // Include backhaul information
            backhaulPrice: backhaulAmount,
            backhaulDescription: shipment.backhaulDescription || '',
            // Include all additional costs (itemized + manual)
            additionalCosts: allAdditionalCosts.map(cost => ({
              description: cost.description || '',
              quantity: cost.quantity || 1,
              unitPrice: cost.unitPrice || cost.amount || 0,
              total: Math.round(((cost.quantity || 1) * (cost.unitPrice || cost.amount || 0)) * 100) / 100,
              type: cost.type || 'other',
              source: cost.source || 'manual'
            }))
          };
        })
      };
      
      console.log('📋 Final invoice structure:', newInvoice);

      // Clean undefined values before sending to Firestore
      const cleanedInvoice = cleanUndefinedValues(newInvoice);
      console.log('📋 Cleaned invoice structure:', cleanedInvoice);

      // Final validation of required fields
      const requiredFields = ['invoiceNumber', 'companyId', 'clientId', 'subtotal', 'vatAmount', 'total'];
      const missingFields = requiredFields.filter(field => 
        cleanedInvoice[field] === null || cleanedInvoice[field] === undefined
      );
      
      if (missingFields.length > 0) {
        throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
      }

      // Create the invoice
      console.log('🔄 Creating invoice in database...');
      const firestoreDocId = await invoicesService.addInvoice(cleanedInvoice);
      console.log('✅ Invoice created successfully!');
      console.log('📋 Firestore Document ID:', firestoreDocId);
      console.log('📋 Invoice Number:', invoiceNumber);

      // Optional: Sync additional costs to shipment costing
      if (allAdditionalCosts.length > 0) {
        console.log('🔄 Syncing additional costs to shipment costing...');
        try {
          await Promise.all(
            selectedShipments.map(async (shipment) => {
              const shipmentCost = shipmentCosts[shipment.id];
              if (shipmentCost?.additionalCosts?.length > 0) {
                try {
                  const existingCosting = await shipmentCostingsService.getCostingByShipment(shipment.id);
                  if (existingCosting) {
                    // Only sync new costs (not existing ones)
                    const newCosts = shipmentCost.additionalCosts.filter(cost => cost.source !== 'existing');
                    
                    if (newCosts.length > 0) {
                      const updatedOtherCosts = [
                        ...(existingCosting.otherCosts || []),
                        ...newCosts.filter(cost => cost.type === 'other').map(cost => ({
                          description: cost.description,
                          amount: cost.amount,
                          invoiceId: firestoreDocId,
                          createdAt: new Date().toISOString()
                        }))
                      ];
                      
                      const updatedStops = [
                        ...(existingCosting.stops || []),
                        ...newCosts.filter(cost => cost.type === 'stop').map(cost => ({
                          description: cost.description,
                          amount: cost.amount,
                          invoiceId: firestoreDocId,
                          createdAt: new Date().toISOString()
                        }))
                      ];

                      await shipmentCostingsService.updateCosting(existingCosting.id, {
                        otherCosts: updatedOtherCosts,
                        stops: updatedStops
                      });
                    }
                  }
                } catch (costingError) {
                  console.warn(`⚠️ Failed to update costing for shipment ${shipment.id}:`, costingError);
                }
              }
            })
          );
          console.log('✅ Costing sync completed');
        } catch (costingError) {
          console.warn('⚠️ Costing sync failed (non-critical):', costingError);
        }
      }

      console.log('🎉 Invoice creation completed successfully!');
      toast.success(t('accounting.success.created'));
      
      // Call onSuccess to refresh the invoices list
      if (onSuccess) {
        onSuccess();
      }
      
      onClose();
      
    } catch (error) {
      console.error('🚨 Detailed error creating invoice:', error);
      
      // Specific error handling
      if (error?.code === 'failed-precondition') {
        toast.error(t('invoices.error.index_required', 'Database index required. Please contact support.'));
      } else if (error?.code === 'permission-denied') {
        toast.error(t('invoices.error.permission_denied', 'Permission denied. Check your access rights.'));
      } else if (error?.code === 'unavailable') {
        toast.error(t('invoices.error.service_unavailable', 'Service temporarily unavailable. Please try again.'));
      } else if (error?.message?.includes('Missing or insufficient permissions')) {
        toast.error(t('invoices.error.permissions', 'Insufficient permissions to create invoice.'));
      } else {
        toast.error(t('invoices.error.creation_failed', `Failed to create invoice: ${error?.message || 'Unknown error'}`));
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get shipment status badge with appropriate styling
   */
  const getShipmentStatusBadge = (shipment) => {
    const { invoiceStatus, relatedInvoices } = shipment;
    
    if (invoiceStatus === 'available') {
      return <span className="badge text-white bg-green-600 border-green-600">✅ Available</span>;
    } else if (invoiceStatus === 'draft') {
      const draftInvoice = relatedInvoices?.find(inv => inv.status === 'draft');
      return (
        <span className="badge text-white bg-amber-600 border-amber-600">
          ⚠️ {t('common.draft_invoice', 'Draft Invoice')} #{draftInvoice?.invoiceId || 'Unknown'}
        </span>
      );
    } else if (invoiceStatus === 'invoiced') {
      const activeInvoice = relatedInvoices?.find(inv => inv.status !== 'cancelled' && inv.status !== 'draft');
      return (
        <span className="badge text-white bg-red-600 border-red-600">
          ❌ Invoiced ({activeInvoice?.invoiceId || 'Unknown'} - {activeInvoice?.status || 'Unknown'})
        </span>
      );
    }
    return <span className="badge text-white bg-gray-600 border-gray-600">Unknown</span>;
  };

  /**
   * Group shipments by client and availability status
   */
  const groupedShipments = deliveredShipments.reduce((acc, shipment) => {
    const clientId = shipment.clientId || 'unknown';
    const clientName = shipment.clientName || 'Unknown Client';
    
    if (!acc[clientId]) {
      acc[clientId] = {
        clientName,
        clientId,
        availableShipments: [],
        draftShipments: [],
        invoicedShipments: [],
        totalAvailableAmount: 0,
        totalDraftAmount: 0,
        totalInvoicedAmount: 0
      };
    }
    
    if (shipment.invoiceStatus === 'available') {
      acc[clientId].availableShipments.push(shipment);
      acc[clientId].totalAvailableAmount += shipment.totalAmount;
    } else if (shipment.invoiceStatus === 'draft') {
      acc[clientId].draftShipments.push(shipment);
      acc[clientId].totalDraftAmount += shipment.totalAmount;
    } else if (shipment.invoiceStatus === 'invoiced') {
      acc[clientId].invoicedShipments.push(shipment);
      acc[clientId].totalInvoicedAmount += shipment.totalAmount;
    }
    
    return acc;
  }, {});

  // Sort shipments within each group by date (latest first)
  Object.values(groupedShipments).forEach(group => {
    const sortByDate = (a, b) => {
      const dateA = a.displayDateTime || a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
      const dateB = b.displayDateTime || b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
      return new Date(dateB) - new Date(dateA); // Latest first
    };
    
    group.availableShipments.sort(sortByDate);
    group.draftShipments.sort(sortByDate);
    group.invoicedShipments.sort(sortByDate);
  });

  const { subtotal, vatAmount, total } = calculateInvoiceTotals();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div>
            <h3 className="text-2xl font-bold text-gray-900">
              {t('accounting.create_invoice', 'Create Invoice')}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {t('invoices.create_from_shipments_desc', 'Create invoice from delivered shipments')}
            </p>
          </div>
          <button 
            onClick={onClose} 
            className="btn btn-sm btn-circle btn-ghost hover:bg-gray-100"
            aria-label={t('common.close')}
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Stepper Navigation */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-center gap-8">
            {[1,2,3].map((s, idx) => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-200 ${step === s ? 'bg-blue-600 text-white scale-110 shadow-lg' : 'bg-gray-200 text-gray-600'}`}>{s}</div>
                <span className={`font-medium ${step === s ? 'text-blue-700' : 'text-gray-500'}`}>{[
                  t('invoices.step.select_shipments', 'Select Shipments'),
                  t('invoices.step.invoice_details', 'Invoice Details'),
                  t('invoices.step.review_create', 'Review & Create')
                ][idx]}</span>
                {s < 3 && <div className={`w-10 h-1 rounded ${step > s ? 'bg-blue-600' : 'bg-gray-200'}`}/>} 
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading && (
            <div className="flex justify-center items-center h-64">
              <LoadingSpinner size="lg" />
              <span className="ml-3 text-gray-600">{t('common.loading_shipments', 'Loading shipments...')}</span>
            </div>
          )}

          {/* Step 1: Select Shipments */}
          {!loading && step === 1 && (
            <>
              <h4 className="text-lg font-semibold mb-4">{t('invoices.step.select_shipments', 'Select Shipments')}</h4>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                  <InformationCircleIcon className="w-5 h-5 text-blue-600 mr-2" />
                  <p className="text-sm text-blue-800">
                    {t('invoices.select_shipments_help', 'Select delivered shipments to include in the invoice. Only shipments that haven\'t been invoiced yet are shown.')}
                  </p>
                </div>
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start">
                    <InformationCircleIcon className="w-4 h-4 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-yellow-800">
                      <p className="font-medium mb-1">{t('invoices.delivery_status_required', 'Delivery Status Required')}</p>
                      <p>{t('invoices.delivery_status_explanation', 'Shipments must be marked as "Delivered" in the shipments section before they can be selected for invoicing. If you see shipments listed but cannot select them, please update their status to "Delivered" first.')}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {Object.keys(groupedShipments).length === 0 ? (
                <div className="text-center py-12">
                  <DocumentTextIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {t('invoices.no_shipments_available', 'No shipments available')}
                  </h3>
                  <p className="text-gray-500">
                    {t('invoices.no_shipments_desc', 'There are no delivered shipments ready for invoicing.')}
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {Object.values(groupedShipments).map((group) => {
                    const availableShipments = group.availableShipments || [];
                    const draftShipments = group.draftShipments || [];
                    const invoicedShipments = group.invoicedShipments || [];
                    
                    const totalAvailableAndDraft = [...availableShipments, ...draftShipments];
                    const allSelected = totalAvailableAndDraft.length > 0 && totalAvailableAndDraft.every(s => 
                      selectedShipments.some(selected => selected.id === s.id)
                    );
                    const someSelected = totalAvailableAndDraft.some(s => 
                      selectedShipments.some(selected => selected.id === s.id)
                    );
                    
                    // Check if any other client has selected shipments
                    const hasOtherClientSelected = selectedShipments.some(s => s.clientId !== group.clientId);
                    const isThisClientSelected = selectedShipments.some(s => s.clientId === group.clientId);
                    const isDisabled = hasOtherClientSelected && !isThisClientSelected;
                    
                    return (
                      <div key={group.clientId} className={`border border-gray-200 rounded-lg overflow-hidden ${isDisabled ? 'opacity-50' : ''}`}>
                        {/* Client Header */}
                        <div className={`px-6 py-4 border-b border-gray-200 flex justify-between items-center ${isDisabled ? 'bg-gray-100' : 'bg-gray-50'}`}>
                          <div className="flex items-center gap-4">
                            {totalAvailableAndDraft.length > 0 && (
                              <input
                                type="checkbox"
                                checked={allSelected}
                                ref={input => { if (input) input.indeterminate = someSelected && !allSelected; }}
                                onChange={(e) => handleSelectAllForClient(group.clientId, totalAvailableAndDraft, e.target.checked)}
                                disabled={isDisabled}
                                className={`w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                              />
                            )}
                            <div>
                              <h4 className={`text-lg font-medium ${isDisabled ? 'text-gray-500' : 'text-gray-900'}`}>{group.clientName}</h4>
                              <p className={`text-sm ${isDisabled ? 'text-gray-400' : 'text-gray-500'}`}>
                                {availableShipments.length + draftShipments.length} {t('invoices.selectable_shipments', 'selectable shipments')}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`text-lg font-semibold ${isDisabled ? 'text-gray-500' : 'text-gray-900'}`}>
                              {formatCurrency((group.totalAvailableAmount || 0) + (group.totalDraftAmount || 0))}
                            </p>
                            <p className={`text-sm ${isDisabled ? 'text-gray-400' : 'text-gray-500'}`}>{t('invoices.available_amount', 'Available Amount')}</p>
                          </div>
                        </div>
                        
                        <div className="p-4 space-y-3">
                          {/* Available Shipments Section */}
                          {availableShipments.length > 0 && (
                            <div className="border border-green-200 rounded-lg overflow-hidden">
                              <div 
                                className={`px-4 py-3 border-b border-green-200 flex justify-between items-center ${isDisabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-green-50 cursor-pointer'}`}
                                onClick={() => !isDisabled && toggleSection(group.clientId, 'available')}
                              >
                                <div className="flex items-center gap-3">
                                  <input
                                    type="checkbox"
                                    checked={availableShipments.every(s => selectedShipments.some(sel => sel.id === s.id))}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      handleSelectAllForClient(group.clientId, availableShipments, e.target.checked);
                                    }}
                                    disabled={isDisabled}
                                    className={`w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                  />
                                  {collapsedSections[`${group.clientId}-available`] ? (
                                    <ChevronRightIcon className="w-4 h-4 text-gray-600" />
                                  ) : (
                                    <ChevronDownIcon className="w-4 h-4 text-gray-600" />
                                  )}
                                  <div>
                                    <h5 className={`font-medium ${isDisabled ? 'text-gray-500' : 'text-green-800'}`}>✅ {t('invoices.available_shipments', 'Available Shipments')}</h5>
                                    <p className={`text-xs ${isDisabled ? 'text-gray-400' : 'text-green-700'}`}>{availableShipments.length} {t('common.shipments', 'shipments')}</p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className={`font-semibold ${isDisabled ? 'text-gray-500' : 'text-green-800'}`}>{formatCurrency(group.totalAvailableAmount || 0)}</p>
                                </div>
                              </div>
                              
                              {!collapsedSections[`${group.clientId}-available`] && (
                                <div className="divide-y divide-green-100">
                                  {availableShipments.map((shipment) => (
                                    <div key={shipment.id} className={`p-4 flex items-center justify-between ${isDisabled ? 'opacity-50' : 'hover:bg-green-25'}`}>
                                      <div className="flex items-center gap-3">
                                        <input
                                          type="checkbox"
                                          checked={selectedShipments.some(s => s.id === shipment.id)}
                                          onChange={(e) => handleShipmentSelection(shipment, e.target.checked)}
                                          disabled={isDisabled}
                                          className={`w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        />
                                                                                  <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                              <div className={`font-medium ${isDisabled ? 'text-gray-500' : 'text-gray-900'}`}>{shipment.trackingNumber || shipment.id}</div>
                                              {getShipmentStatusBadge(shipment)}
                                            </div>
                                            <div className={`text-sm ${isDisabled ? 'text-gray-400' : 'text-gray-600'}`}>{shipment.origin} → {shipment.destination}</div>
                                            <div className={`flex items-center gap-4 text-xs ${isDisabled ? 'text-gray-400' : 'text-gray-500'}`}>
                                              <span>Trip: {shipment.tripNumber || '-'}</span>
                                              <span>Date: {shipment.displayDateTime ? new Date(shipment.displayDateTime).toLocaleDateString() : '-'}</span>
                                              <span>Service: {shipment.serviceType || '-'}</span>
                                            </div>
                                            
                                            {/* Show sub-charges breakdown */}
                                            {((shipment.itemizedCosts && shipment.itemizedCosts.length > 0) || shipment.backhaulPrice > 0) && (
                                              <div className={`mt-2 p-2 bg-blue-50 rounded text-xs ${isDisabled ? 'opacity-50' : ''}`}>
                                                <div className={`font-medium mb-1 ${isDisabled ? 'text-gray-500' : 'text-blue-800'}`}>Additional Charges:</div>
                                                <div className="space-y-1">
                                                  <div className={`flex justify-between ${isDisabled ? 'text-gray-400' : 'text-gray-600'}`}>
                                                    <span>Base Service:</span>
                                                    <span>{formatCurrency(shipment.baseAmount || shipment.cost || 0)}</span>
                                                  </div>
                                                  {shipment.itemizedCosts && shipment.itemizedCosts.map((item, idx) => (
                                                    <div key={idx} className={`flex justify-between ${isDisabled ? 'text-gray-400' : 'text-gray-600'}`}>
                                                      <span>• {item.description}:</span>
                                                      <span>{formatCurrency(item.total || (item.quantity * item.unitPrice) || 0)}</span>
                                                    </div>
                                                  ))}
                                                  {shipment.backhaulPrice > 0 && (
                                                    <div className={`flex justify-between ${isDisabled ? 'text-gray-400' : 'text-gray-600'}`}>
                                                      <span>• Backhaul:</span>
                                                      <span>{formatCurrency(shipment.backhaulPrice)}</span>
                                                    </div>
                                                  )}
                                                  <div className={`flex justify-between font-medium pt-1 border-t border-blue-200 ${isDisabled ? 'text-gray-500' : 'text-blue-800'}`}>
                                                    <span>Subtotal:</span>
                                                    <span>{formatCurrency((shipment.baseAmount || shipment.cost || 0) + (shipment.itemizedCosts || []).reduce((sum, item) => sum + (item.total || (item.quantity * item.unitPrice) || 0), 0) + (shipment.backhaulPrice || 0))}</span>
                                                  </div>
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                      </div>
                                      <div className="text-right">
                                        <span className={`font-medium ${isDisabled ? 'text-gray-500' : 'text-gray-900'}`}>{formatCurrency(shipment.totalAmount)}</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* Summary and Navigation */}
              <div className="mt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="text-sm text-gray-600">
                  <span className="font-medium">{ensureEnglishNumerals(selectedShipments.length)}</span> {t('invoices.shipments_selected', 'shipment(s) selected')} • <span className="font-medium">{formatCurrency(total)}</span> {t('invoices.total', 'total')}
                </div>
                <div className="flex gap-2">
                  <button onClick={onClose} className="btn btn-outline">{t('common.cancel', t('common.cancel'))}</button>
                  <button 
                    onClick={() => setStep(2)}
                    className="btn btn-primary"
                    disabled={selectedShipments.length === 0}
                  >
                    {t('invoices.continue_to_details', 'Continue to Details')}
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Step 2: Invoice Details */}
          {!loading && step === 2 && (
            <>
              <h4 className="text-lg font-semibold mb-4">{t('invoices.step.invoice_details', 'Invoice Details')}</h4>
              
              {/* Basic Invoice Information */}
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h4 className="text-lg font-semibold mb-4">{t('invoices.invoice_details', 'Invoice Details')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('invoices.client', 'Client')} *</label>
                    <select
                      value={invoiceData.clientId}
                      onChange={(e) => setInvoiceData(prev => ({ ...prev, clientId: e.target.value }))}
                      className="select select-bordered w-full"
                      required
                    >
                      <option value="">{t('invoices.select_client', 'Select a client')}</option>
                      {clients.map(client => (
                        <option key={client.id} value={client.id}>
                          {client.companyName || client.contactPerson || client.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('invoices.issue_date', 'Issue Date')} *</label>
                    <input
                      type="date"
                      value={invoiceData.issueDate}
                      onChange={(e) => setInvoiceData(prev => ({ ...prev, issueDate: e.target.value }))}
                      className="input input-bordered w-full"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('invoices.due_date', 'Due Date')} *</label>
                    <input
                      type="date"
                      value={invoiceData.dueDate}
                      onChange={(e) => setInvoiceData(prev => ({ ...prev, dueDate: e.target.value }))}
                      className="input input-bordered w-full"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('invoices.description', t('common.description'))}</label>
                    <input
                      type="text"
                      value={invoiceData.description}
                      onChange={(e) => setInvoiceData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder={t('invoices.invoice_description_placeholder', 'Invoice description')}
                      className="input input-bordered w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('invoices.vat_id', 'VAT ID')}</label>
                    <input
                      type="text"
                      value={invoiceData.vatId}
                      onChange={(e) => setInvoiceData(prev => ({ ...prev, vatId: e.target.value }))}
                      placeholder={t('invoices.vat_id_placeholder', 'Enter VAT ID (optional)')}
                      className="input input-bordered w-full"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('invoices.notes', t('common.notes'))}</label>
                    <textarea
                      value={invoiceData.notes}
                      onChange={(e) => setInvoiceData(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder={t('invoices.notes_placeholder', 'Additional notes for this invoice')}
                      rows={3}
                      className="textarea textarea-bordered w-full"
                    />
                  </div>
                </div>
              </div>

              {/* Navigation */}
              <div className="flex justify-between">
                <button onClick={() => setStep(1)} className="btn btn-outline">
                  {t('common.back', 'Back')}
                </button>
                <button onClick={() => setStep(3)} className="btn btn-primary">
                  {t('invoices.review_invoice', 'Review Invoice')}
                </button>
              </div>
            </>
          )}

          {/* Step 3: Review & Create */}
          {!loading && step === 3 && (
            <>
              <h4 className="text-lg font-semibold mb-4">{t('invoices.step.review_create', 'Review & Create')}</h4>
              
              {/* Invoice Summary */}
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h5 className="font-semibold mb-4">{t('invoices.invoice_summary', 'Invoice Summary')}</h5>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">{t('invoices.client', 'Client')}:</span>
                    <span className="ml-2 font-medium">
                      {(() => {
                        const client = clients.find(c => c.id === invoiceData.clientId);
                        if (!client) return 'Unknown Client';
                        return client.companyName || client.contactPerson || client.name || 'Unknown Client';
                      })()}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">{t('invoices.shipments', 'Shipments')}:</span>
                    <span className="ml-2 font-medium">{selectedShipments.length}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">{t('invoices.subtotal', 'Subtotal')}:</span>
                    <span className="ml-2 font-medium">{formatCurrency(subtotal)}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">{t('invoices.vat', 'VAT (15%)')}:</span>
                    <span className="ml-2 font-medium">{formatCurrency(vatAmount)}</span>
                  </div>
                  <div className="col-span-2 pt-2 border-t">
                    <span className="text-gray-900 font-semibold">{t('invoices.total', t('common.total'))}:</span>
                    <span className="ml-2 text-lg font-bold text-blue-600">{formatCurrency(total)}</span>
                  </div>
                </div>
              </div>

              {/* Navigation */}
              <div className="flex justify-between">
                <button onClick={() => setStep(2)} className="btn btn-outline">
                  {t('common.back', 'Back')}
                </button>
                <button 
                  onClick={createInvoice}
                  disabled={loading}
                  className="btn btn-primary"
                >
                  {loading ? (
                    <>
                      <LoadingSpinner size="sm" />
                      {t('invoices.creating', 'Creating...')}
                    </>
                  ) : (
                    t('invoices.create_invoice', 'Create Invoice')
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateInvoiceModal;